import {
  CREATE_EVENT_SUCCESS,
  CREATE_EVENT_FAIL,
  GET_EVENT_REQUEST,
  GET_EVENT_SUCCESS,
  GET_EVENT_FAIL,
  GET_EVENTS_REQUEST,
  GET_EVENTS_SUCCESS,
  GET_EVENTS_FAIL,
  GET_PROMOTER_EVENTS_REQUEST,
  GET_PROMOTER_EVENTS_SUCCESS,
  GET_PROMOTER_EVENTS_FAIL,
  GET_EVENT_MESSAGES_REQUEST,
  GET_EVENT_MESSAGES_SUCCESS,
  GET_EVENT_MESSAGES_FAIL,
  CREATE_EVENT_MESSAGE_REQUEST,
  CREATE_EVENT_MESSAGE_SUCCESS,
  CREATE_EVENT_MESSAGE_FAIL,
  DELETE_EVENT_MESSAGE_REQUEST,
  DELETE_EVENT_MESSAGE_SUCCESS,
  DELETE_EVENT_MESSAGE_FAIL,
  SEARCH_EVENTS_REQUEST,
  SEARCH_EVENTS_SUCCESS,
  SEARCH_EVENTS_FAIL,
  UPDATE_EVENT_SUCCESS,
  UPDATE_EVENT_FAIL,
  CANCEL_EVENT_SUCCESS,
  CANCEL_EVENT_FAIL,
  PUBLISH_EVENT_SUCCESS,
  DELETE_EVENT_SUCCESS,
  DELETE_EVENT_FAIL,
  INVITE_BRAND_REQUEST,
  INVITE_BRAND_SUCCESS,
  INVITE_BRAND_FAIL,
  GET_TEMPLATE_EVENTS_REQUEST,
  GET_TEMPLATE_EVENTS_SUCCESS,
  GET_TEMPLATE_EVENTS_FAIL,
  DUPLICATE_EVENT_REQUEST,
  DUPLICATE_EVENT_SUCCESS,
  DUPLICATE_EVENT_FAIL,
} from '../actions/events';

const initialState = {
  events: [],
  eventsPagination: null,
  promoterEvents: [],
  eventDetails: {
    event: null,
    loading: false,
    error: null,
  },
  eventMessages: {
    byEventId: {},
  },
  templateEvents: [],
  templateEventsPagination: null,
  templateEventsLoading: false,
  duplicating: false,
  last: null,
  loading: false,
  error: null,
};

// Helper to get or create a per-event messages slice
const getEventSlice = (state, eventId) =>
  state.eventMessages.byEventId[eventId] || {
    messages: [],
    loading: false,
    error: null,
  };

const setEventSlice = (state, eventId, patch) => ({
  ...state,
  eventMessages: {
    ...state.eventMessages,
    byEventId: {
      ...state.eventMessages.byEventId,
      [eventId]: { ...getEventSlice(state, eventId), ...patch },
    },
  },
});

const eventReducer = (state = initialState, action) => {
  switch (action.type) {
    case CREATE_EVENT_SUCCESS:
      return {
        ...state,
        events: [...state.events, action.payload],
        loading: false,
        error: null,
      };
    case CREATE_EVENT_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case GET_EVENTS_REQUEST: {
      const append = Boolean(action.meta?.append);
      const resetId = action.meta?.resetId;
      return {
        ...state,
        loading: true,
        error: null,
        ...(append
          ? {}
          : {
              events: [],
              eventsPagination: null,
              latestResetId: resetId ?? state.latestResetId,
            }),
      };
    }
    case GET_PROMOTER_EVENTS_REQUEST:
      return {
        ...state,
        loading: true,
      };
    case GET_EVENTS_SUCCESS: {
      const payload = action.payload;
      const resetId = action.meta?.resetId;

      // Drop stale responses from a previous sort/filter generation
      if (
        resetId != null &&
        state.latestResetId != null &&
        resetId < state.latestResetId
      ) {
        return state;
      }

      // Back-compat: if payload is raw array
      if (Array.isArray(payload)) {
        return {
          ...state,
          events: payload,
          eventsPagination: null,
          loading: false,
          error: null,
        };
      }

      const { data = [], pagination = null, append = false } = payload || {};
      return {
        ...state,
        events: append ? [...state.events, ...data] : data,
        eventsPagination: pagination,
        loading: false,
        error: null,
      };
    }

    case GET_EVENT_REQUEST:
      return {
        ...state,
        eventDetails: { event: null, loading: true, error: null },
      };
    // A cancellation is just a status patch, so it lands the same way an
    // edit does — same lists, same shallow merge by id.
    case CANCEL_EVENT_SUCCESS:
    case PUBLISH_EVENT_SUCCESS:
    case UPDATE_EVENT_SUCCESS: {
      const updated = action.payload;
      const patch = (arr) =>
        Array.isArray(arr)
          ? arr.map((e) => (e?.id === updated?.id ? { ...e, ...updated } : e))
          : arr;

      return {
        ...state,
        loading: false,
        error: null,
        events: patch(state.events),
        promoterEvents: patch(state.promoterEvents),
        eventDetails: {
          ...state.eventDetails,
          loading: false,
          error: null,
          event:
            state.eventDetails.event?.id === updated?.id
              ? { ...state.eventDetails.event, ...updated }
              : state.eventDetails.event,
        },
      };
    }

    case CANCEL_EVENT_FAIL:
    case UPDATE_EVENT_FAIL:
      return { ...state, loading: false, error: action.payload };

    case DELETE_EVENT_SUCCESS: {
      const deletedId = action.payload;
      const drop = (arr) =>
        Array.isArray(arr) ? arr.filter((e) => e?.id !== deletedId) : arr;

      const isCurrent = state.eventDetails.event?.id === deletedId;

      return {
        ...state,
        loading: false,
        error: null,
        events: drop(state.events),
        promoterEvents: drop(state.promoterEvents),
        eventDetails: isCurrent
          ? { event: null, loading: false, error: null }
          : state.eventDetails,
      };
    }

    case DELETE_EVENT_FAIL:
      return { ...state, loading: false, error: action.payload };

    case GET_PROMOTER_EVENTS_SUCCESS:
      return {
        ...state,
        promoterEvents: action.payload,
        loading: false,
        error: null,
      };
    case GET_EVENTS_FAIL: {
      const resetId = action.meta?.resetId;
      if (
        resetId != null &&
        state.latestResetId != null &&
        resetId < state.latestResetId
      ) {
        return state;
      }
      return { ...state, loading: false, error: action.payload };
    }
    case GET_PROMOTER_EVENTS_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case GET_EVENT_SUCCESS:
      return {
        ...state,
        eventDetails: {
          event: action.payload,
          loading: false,
          error: null,
        },
      };
    case GET_EVENT_FAIL:
      return {
        ...state,
        eventDetails: {
          event: null,
          loading: false,
          error: action.payload,
        },
      };
    case GET_EVENT_MESSAGES_REQUEST:
      return setEventSlice(state, action.meta.eventId, {
        loading: true,
        error: null,
      });
    case GET_EVENT_MESSAGES_SUCCESS:
      return setEventSlice(state, action.meta.eventId, {
        loading: false,
        messages: action.payload,
        error: null,
      });
    case GET_EVENT_MESSAGES_FAIL:
      return setEventSlice(state, action.meta.eventId, {
        loading: false,
        error: action.payload,
      });

    case CREATE_EVENT_MESSAGE_REQUEST:
      return setEventSlice(state, action.meta.eventId, {
        loading: true,
      });
    case CREATE_EVENT_MESSAGE_SUCCESS: {
      const slice = getEventSlice(state, action.meta.eventId);
      return setEventSlice(state, action.meta.eventId, {
        loading: false,
        messages: [action.payload, ...slice.messages],
        error: null,
      });
    }
    case CREATE_EVENT_MESSAGE_FAIL:
      return setEventSlice(state, action.meta.eventId, {
        loading: false,
        error: action.payload,
      });

    case DELETE_EVENT_MESSAGE_REQUEST:
      return setEventSlice(state, action.meta.eventId, {
        loading: true,
      });
    case DELETE_EVENT_MESSAGE_SUCCESS: {
      const slice = getEventSlice(state, action.meta.eventId);
      return setEventSlice(state, action.meta.eventId, {
        loading: false,
        messages: slice.messages.filter((msg) => msg.id !== action.payload),
        error: null,
      });
    }
    case DELETE_EVENT_MESSAGE_FAIL:
      return setEventSlice(state, action.meta.eventId, {
        loading: false,
        error: action.payload,
      });
    case SEARCH_EVENTS_REQUEST: {
      const resetId = action.meta?.resetId;
      return {
        ...state,
        loading: true,
        error: null,
        // Clear accumulated list immediately so stale items can't show
        // through cache fallbacks while the search runs.
        events: [],
        eventsPagination: null,
        latestResetId: resetId ?? state.latestResetId,
      };
    }
    case SEARCH_EVENTS_SUCCESS: {
      const resetId = action.meta?.resetId;
      if (
        resetId != null &&
        state.latestResetId != null &&
        resetId < state.latestResetId
      ) {
        return state;
      }
      return {
        ...state,
        events: action.payload,
        eventsPagination: null,
        loading: false,
        error: null,
      };
    }
    case SEARCH_EVENTS_FAIL: {
      const resetId = action.meta?.resetId;
      if (
        resetId != null &&
        state.latestResetId != null &&
        resetId < state.latestResetId
      ) {
        return state;
      }
      return { ...state, loading: false, error: action.payload };
    }

    case INVITE_BRAND_REQUEST:
      return {
        ...state,
        inviteBrand: { loading: true, error: null, last: null },
      };

    case INVITE_BRAND_SUCCESS:
      return {
        ...state,
        inviteBrand: { loading: false, error: null, last: action.payload },
      };

    case INVITE_BRAND_FAIL:
      return {
        ...state,
        inviteBrand: { loading: false, error: action.payload, last: null },
      };

    case GET_TEMPLATE_EVENTS_REQUEST:
      return { ...state, templateEventsLoading: true };
    case GET_TEMPLATE_EVENTS_SUCCESS:
      return {
        ...state,
        templateEventsLoading: false,
        templateEvents: action.payload.data,
        templateEventsPagination: action.payload.pagination,
      };
    case GET_TEMPLATE_EVENTS_FAIL:
      return { ...state, templateEventsLoading: false, error: action.payload };

    case DUPLICATE_EVENT_REQUEST:
      return { ...state, duplicating: true };
    case DUPLICATE_EVENT_SUCCESS:
      return {
        ...state,
        duplicating: false,
        promoterEvents: [...state.promoterEvents, action.payload],
      };
    case DUPLICATE_EVENT_FAIL:
      return { ...state, duplicating: false, error: action.payload };

    default:
      return state;
  }
};

export default eventReducer;
